/* eslint-disable @typescript-eslint/no-redeclare */
import { makeBetterEnum } from './betterEnum';

export const BiProductFootprintArtifactKind = makeBetterEnum(
  'PurchaseOrders',
  'MaterialMaster'
);

export type BiProductFootprintArtifactKind =
  (typeof BiProductFootprintArtifactKind)[keyof typeof BiProductFootprintArtifactKind];

export const UrlBiMode = makeBetterEnum('materials', 'purchases');
export type UrlBiMode = (typeof UrlBiMode)[keyof typeof UrlBiMode];

export const DEFAULT_FLASH_CREDITS = 100;
export const DEFAULT_PROXY_CREDITS = 10_000;

export const MAX_CONCURRENT_FLASH_RUNS = 10;

export const LANGFUSE_PRODUCTION_GRAPH_PROJECT_ID = 'cm9jb21r702kvad079e73t9nm';

export function langfuseSessionUrl(sessionId: string): string {
  return `https://us.cloud.langfuse.com/project/${LANGFUSE_PRODUCTION_GRAPH_PROJECT_ID}/sessions/${sessionId}`;
}

// Keep in sync with watershed.db.regional.models.lifecycle_assessment.LifecycleAssessmentCreationSource
export const LifecycleAssessmentCreationSource = makeBetterEnum(
  'AiPoweredMaterialMapping',
  'AiPoweredProductionGraph',
  'UserSelectedMaterialMapping',
  'CustomProductionGraph',
  'SupplierLcaUpload',
  'Unknown'
);

export type LifecycleAssessmentCreationSource =
  (typeof LifecycleAssessmentCreationSource)[keyof typeof LifecycleAssessmentCreationSource];

// Keep in sync with watershed.db.regional.models.lifecycle_assessment.BoundaryEnum
export const BoundaryDisplayName = makeBetterEnum(
  'GateToGate',
  'CradleToGate',
  'CradleToGrave',
  'CradleToSite'
);

export type BoundaryDisplayName =
  (typeof BoundaryDisplayName)[keyof typeof BoundaryDisplayName];

export const PfArtifactStatus = makeBetterEnum(
  'NotStarted',
  'InProgress',
  'Completed',
  'Errored'
);

export type PfArtifactStatus =
  (typeof PfArtifactStatus)[keyof typeof PfArtifactStatus];

// Keep in sync with PurchaseOrderMappingTaskWorkflowStatus in watershed.db.regional.models.purchase_order_mapping_task
export const PurchaseOrderMappingTaskWorkflowStatus = makeBetterEnum(
  'IN_PROGRESS',
  'COMPLETED',
  'ERRORED'
);

export type PurchaseOrderMappingTaskWorkflowStatus =
  (typeof PurchaseOrderMappingTaskWorkflowStatus)[keyof typeof PurchaseOrderMappingTaskWorkflowStatus];

export const BulkPcfSelectionType = makeBetterEnum(
  'latestProxyMapping',
  'highestMaterialMatchProxyMapping',
  'highestEmissionsMatchProxyMapping',
  'latestAdvancedProductionGraph',
  'latestCustomProductionGraph',
  'latestSupplierPcfUpload',
  'unselectPcf',
  'selectMaterialFromLibrary',
  'latestSupplierPcfUploadByValidityEndDate',
  'latestSupplierPcfUploadByReferenceEndDate'
);

export type BulkPcfSelectionType =
  (typeof BulkPcfSelectionType)[keyof typeof BulkPcfSelectionType];

export const ProductionGraphReviewStatus = makeBetterEnum(
  'ReviewPending',
  'DoesNotMeetCriteria',
  'MeetsCriteria'
);

export type ProductionGraphReviewStatus =
  (typeof ProductionGraphReviewStatus)[keyof typeof ProductionGraphReviewStatus];

export const ProductionGraphReviewCriteria = makeBetterEnum('ReviewRubricV1');

export type ProductionGraphReviewCriteria =
  (typeof ProductionGraphReviewCriteria)[keyof typeof ProductionGraphReviewCriteria];

/**
 * Individual background data sources available for material library lookups
 * in FLASH, proxy mapping, and lookup-by-id.
 * Keep in sync with py/cliqlink/domain/activity_context.py.
 * [tag: background-data-source-enum]
 */
export const BackgroundDataSource = makeBetterEnum(
  'Ecoinvent',
  'Agrifootprint'
);

export type BackgroundDataSource =
  (typeof BackgroundDataSource)[keyof typeof BackgroundDataSource];

export const MaterialVariantExportFormat = makeBetterEnum(
  'Full',
  'MaterialUpload',
  'SupplierLcaUpload',
  'ProductionGraphUpload'
);
export type MaterialVariantExportFormat =
  (typeof MaterialVariantExportFormat)[keyof typeof MaterialVariantExportFormat];

/**
 * Derives the list of background data sources from the Agri-footprint feature flags.
 * AgrifootprintOnly means only Agri-footprint (no ecoinvent).
 * IncludeAgrifootprint means both ecoinvent and Agri-footprint.
 * Default is ecoinvent only.
 * [ref: background-data-source-enum]
 */
export function deriveBackgroundDataSources(flags: {
  agrifootprintOnly: boolean;
  includeAgrifootprint: boolean;
}): Array<BackgroundDataSource> {
  if (flags.agrifootprintOnly) {
    return [BackgroundDataSource.Agrifootprint];
  }
  if (flags.includeAgrifootprint) {
    return [BackgroundDataSource.Ecoinvent, BackgroundDataSource.Agrifootprint];
  }
  return [BackgroundDataSource.Ecoinvent];
}
